import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import ProgressBar from './ProgressBar';
import {
    CheckCircle2,
    Mail,
    MapPin,
    Bed,
    Users,
    Calendar,
    CreditCard,
    Printer,
    Home,
    Hash,
    Sparkles
} from 'lucide-react';

function Confirmation() {
    const location = useLocation();
    const navigate = useNavigate();

    if (!location.state || !location.state.bookingDetails) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50 flex items-center justify-center">
                <Navbar />
                <div className="text-center p-8">
                    <p className="text-xl text-gray-700">Booking details not found.</p>
                    <button
                        onClick={() => navigate('/beach/home')}
                        className="mt-4 px-6 py-3 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    const { room, guests, bookingDetails, paymentMethod } = location.state;
    const transactionId = Math.floor(Math.random() * 100000000);

    const handlePrint = () => {
        window.print();
    };

    const handleReturnHome = () => {
        navigate('/beach/home');
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">
            <Navbar />
            <ProgressBar currentStep="confirmation" />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                {/* Success Message */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-700 rounded-2xl p-6 md:p-8 shadow-lg"
                >
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
                        <div className="flex-shrink-0">
                            <motion.div
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="bg-green-700 rounded-full p-3 md:p-4"
                            >
                                <CheckCircle2 className="w-8 h-8 md:w-12 md:h-12 text-white" />
                            </motion.div>
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-light text-gray-900 mb-2">
                                Booking Confirmed! 🎉
                            </h1>
                            <p className="text-base md:text-lg text-gray-700">
                                Your booking has been submitted successfully. We've sent a confirmation email to{' '}
                                <span className="font-semibold break-all">{bookingDetails.email}</span>
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Booking Details Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-green-700 mb-6"
                >
                    <div className="border-b border-gray-200 pb-4 mb-6">
                        <h2 className="text-2xl md:text-3xl font-light text-gray-900 text-center flex items-center justify-center gap-2">
                            <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-green-700" />
                            Booking Confirmation
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {/* Transaction ID */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200"
                        >
                            <div className="flex items-start gap-3">
                                <Hash className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Booking ID
                                    </p>
                                    <p className="text-base md:text-lg font-bold text-gray-900 break-all">
                                        {transactionId}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Email */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <Mail className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Email
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900 break-all">
                                        {bookingDetails.email}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {paymentMethod === 'Bank Transfer' && (
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                className="md:col-span-2 bg-amber-50 p-5 rounded-xl border border-amber-200"
                            >
                                <div className="flex items-start gap-3">
                                    <CreditCard className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                    <div className="flex-1">
                                        <p className="text-xs md:text-sm font-medium text-gray-600 mb-2">
                                            Bank Transfer Details
                                        </p>
                                        <div className="space-y-4 text-sm md:text-base text-gray-800">
                                            <div>
                                                <p className="font-semibold">EURO ACCOUNT (EUR)</p>
                                                <ul className="mt-1 space-y-1">
                                                    <li>Account Name: M.N.J. De Silva</li>
                                                    <li>Bank Name: People&apos;s Bank</li>
                                                    <li>Branch: Beruwala</li>
                                                    <li>Account No.: 3114 0715 0030 882</li>
                                                    <li>Address: No.16, Galle Road, Moragalla, Aluthgama 12080, Sri Lanka</li>
                                                    <li>SWIFT Code: PSBKLKLX 023</li>
                                                </ul>
                                            </div>
                                            <div>
                                                <p className="font-semibold">DOLLAR ACCOUNT (USD)</p>
                                                <ul className="mt-1 space-y-1">
                                                    <li>Account Name: M.N.J. De Silva</li>
                                                    <li>Bank Name: Bank of Ceylon</li>
                                                    <li>Branch: Beruwala</li>
                                                    <li>Account No.: 0002980371</li>
                                                    <li>Address: No.16, Galle Road, Moragalla, Aluthgama 12080, Sri Lanka</li>
                                                    <li>SWIFT Code: BCEYLKLX 058</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* City */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        City
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900">
                                        {bookingDetails.city}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Room */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <Bed className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Room Type
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900">
                                        {room.name}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Guests */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <Users className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Guests
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900">
                                        {guests} Adult{guests > 1 ? 's' : ''} + 0 Children
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Check In */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <Calendar className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Check In
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900">
                                        {new Date(room.checkIn).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Check Out */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <Calendar className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Check Out
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900">
                                        {new Date(room.checkOut).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Payment Method */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 p-4 rounded-xl border border-gray-200"
                        >
                            <div className="flex items-start gap-3">
                                <CreditCard className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                                        Payment Method
                                    </p>
                                    <p className="text-sm md:text-base font-medium text-gray-900">
                                        {paymentMethod}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Total Price */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="md:col-span-2 bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border-l-4 border-green-700"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">

                                    <p className="text-base md:text-lg font-bold text-gray-900">
                                        Total Price
                                    </p>
                                </div>
                                <p className="text-2xl md:text-3xl font-bold text-green-700">
                                    €{room.totalPrice.toFixed(2)}
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
                {/* Action Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handlePrint}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl text-sm md:text-base"
                    >
                        <Printer className="w-5 h-5" />
                        Print Booking Details
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.02, boxShadow: '0 10px 25px rgba(16, 185, 129, 0.2)' }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleReturnHome}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl text-sm md:text-base"
                    >
                        <Home className="w-5 h-5" />
                        Return to Home Page
                    </motion.button>
                </div>

                {/* Footer Message */}
                <div className="mt-8 text-center">
                    <p className="text-sm md:text-base text-gray-600">
                        Thank you for choosing Muthumuni Ayurveda Beach Resort!
                    </p>
                    <p className="text-xs md:text-sm text-gray-500 mt-2">
                        If you have any questions, please contact us at{' '}
                        <a href="mailto:muthuabr@yahoo.com" className="text-green-700 hover:underline font-semibold">
                            muthuabr@yahoo.com
                        </a>
                    </p>
                </div>
            </div>

            {/* Print Styles */}
            <style jsx>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .print-section, .print-section * {
                        visibility: visible;
                    }
                    .print-section {
                        position: absolute;
                        left: 0;
                        top: 0;
                    }
                }
            `}</style>
        </div>
    );
}

export default Confirmation;
